import React from 'react'
import PropTypes from 'prop-types'
import { BlockWrapper, IconAction, LocalizedText, Table, Tooltip, UndoIcon } from 'src/components'
import { useHandler } from 'src/hooks'
import { Icon, CheckIcon, RadioIcon, CheckAllIcon } from 'src/components/icons'
import { CounterBadge } from 'src/components/indicators'
import has from 'lodash/has'
import { compareWith } from 'src/util'

function uniqBy(key) {
  return (item, index, array) => array.findIndex(d => d[key] === item[key]) === index
}

const MultiSelectInput = ({ value, onChange, options, label, exclusive, sort = true, ...props }) => {
  const data = React.useMemo(
    () =>
      options

        // normalize options
        .map(option => ({
          name: has(option, 'name') ? option.name : option,
          value: has(option, 'value') ? option.value : option,
          node: has(option, 'node') && option.node,
        }))

        // remove duplicates
        .filter(uniqBy('value'))

        // sort by name
        .sort(sort ? compareWith(({ name }) => name) : () => 0)

        // mark currently selected values
        .map(option => ({
          ...option,
          selected: value.includes(option.value),
        })),
    [options, value, sort]
  )

  const onRowClick = useHandler(({ rowData }) =>
    onChange(
      rowData.selected
        ? exclusive
          ? []
          : value.filter(val => val !== rowData.value)
        : exclusive
        ? [rowData.value]
        : [...value, rowData.value]
    )
  )

  const onReset = useHandler(() => onChange(exclusive || value.length ? [] : data.map(({ value }) => value)))

  /* eslint-disable react/prop-types, react/display-name */
  const colums = [
    {
      width: 100,
      dataKey: 'name',
      cellDataGetter: ({ rowData }) => rowData.name,
      cellRenderer: ({ cellData, rowData }) => rowData.node || cellData,
      flexGrow: 1,
      label,
      disableSort: true,
    },
    {
      width: 35,
      dataKey: 'selected',
      overflow: true,
      align: 'center',
      cellRenderer: ({ rowData }) => (
        <Icon opacity={rowData.selected ? 1 : 0.1} icon={exclusive ? RadioIcon : CheckIcon} />
      ),
      action: (
        <CounterBadge size=".75rem" count={value.length} color="secondary">
          <Tooltip
            disabled={exclusive && !value.length}
            title={<LocalizedText id={exclusive || value.length ? 'WEB_RESET_FILTER' : 'WEB_SELECT_ALL'} />}
          >
            <IconAction
              disabled={exclusive && !value.length}
              icon={exclusive || value.length ? UndoIcon : CheckAllIcon}
              onClick={onReset}
            />
          </Tooltip>
        </CounterBadge>
      ),
      disableSort: true,
    },
  ]

  const isRowHighlighted = useHandler(({ rowData }) => rowData.selected)

  return (
    <BlockWrapper style={{ height: '200px' }}>
      <Table
        columns={colums}
        data={data}
        sortBy="name"
        rowHeight={30}
        headerHeight={35}
        onRowClick={onRowClick}
        isRowHighlighted={isRowHighlighted}
        {...props}
      />
    </BlockWrapper>
  )
  /* eslint-enable react/prop-types, react/display-name */
}

MultiSelectInput.propTypes = {
  options: PropTypes.array,
  sort: PropTypes.bool,
  value: PropTypes.array,
  label: PropTypes.node,
  onChange: PropTypes.func,
  exclusive: PropTypes.bool,
}

MultiSelectInput.defaultProps = {
  options: [],
}

export default MultiSelectInput
